import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { paymentAPI } from "../services/api";
import {
  isBlockedByClient,
  getPaymentErrorMessage,
  logStripeError,
} from "../utils/stripeHelper";

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

// Load Stripe with proper error handling and CORS configuration
const stripePromise = stripePublicKey
  ? loadStripe(stripePublicKey, {
      apiVersion: "2023-10-16",
      locale: "auto",
    }).catch((error) => {
      console.error("Failed to load Stripe:", error);
      return null;
    })
  : null;

const PaymentForm = ({ amount, onSuccess, onError, onLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      const errorMsg =
        "Stripe not loaded. Please check your internet connection and try again.";
      console.error(errorMsg);
      onError(errorMsg);
      return;
    }

    try {
      setLoading(true);
      onLoading(true);

      const amountInRupees = parseInt(amount);
      if (!amountInRupees || amountInRupees < 1 || amountInRupees > 100000) {
        onError("Amount must be between ₹1 and ₹100,000");
        return;
      }

      const amountInPaise = amountInRupees * 100;

      // Create payment intent with timeout
      let response;
      try {
        response = await Promise.race([
          paymentAPI.createPaymentIntent(amountInPaise),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Payment initialization timeout")),
              15000,
            ),
          ),
        ]);
      } catch (error) {
        console.error("Payment intent creation error:", error);
        logStripeError(error, "createPaymentIntent");

        // Check if blocked by client
        if (isBlockedByClient(error)) {
          onError(getPaymentErrorMessage(error));
        } else {
          onError(
            error.response?.data?.message ||
              error.message ||
              "Failed to initialize payment. Try again.",
          );
        }
        return;
      }

      const { clientSecret } = response.data.data;

      // Confirm payment with error handling
      let result;
      try {
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {},
          },
        });
      } catch (error) {
        console.error("Card payment confirmation error:", error);
        onError(
          error.message || "Payment confirmation failed. Please try again.",
        );
        return;
      }

      if (result.error) {
        console.error("Payment result error:", result.error);
        onError(result.error.message);
      } else if (
        result.paymentIntent.status === "succeeded" ||
        result.paymentIntent.status === "processing"
      ) {
        // Confirm on backend
        try {
          await paymentAPI.confirmPayment(result.paymentIntent.id);
          onSuccess();
        } catch (error) {
          console.error("Backend confirmation error:", error);
          onError(
            error.response?.data?.message ||
              "Payment confirmed but failed to add coins. Contact support.",
          );
        }
      }
    } catch (err) {
      console.error("Unexpected payment error:", err);
      logStripeError(err, "AddCoinsModal.handlePayment");

      // Use helper to get friendly error message
      const errorMessage = getPaymentErrorMessage(err);
      onError(errorMessage);
    } finally {
      setLoading(false);
      onLoading(false);
    }
  };

  return (
    <form onSubmit={handlePayment} className="space-y-4">
      <div className="p-3 sm:p-4 border-2 border-gray-300 rounded-lg bg-white">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#fa755a",
              },
            },
          }}
        />
      </div>
      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full py-2 sm:py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Processing..." : "Pay Now"}
      </button>
    </form>
  );
};

const AddCoinsModal = ({ isOpen, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("100");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const presetAmounts = [50, 100, 500, 1000, 2500, 5000];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={!loading ? onClose : undefined}
    >
      <div
        className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
          Add Coins
        </h2>
        <p className="text-gray-600 text-sm sm:text-base mb-6">
          1 Rupee = 1 Coin
        </p>

        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border-2 border-red-400 rounded-lg">
            <p className="text-sm sm:text-base text-red-600 font-semibold">
              ❌ {error}
            </p>
          </div>
        )}

        {/* Preset amounts */}
        <div className="mb-6">
          <p className="text-sm sm:text-base font-semibold text-gray-700 mb-3">
            Quick select:
          </p>
          <div className="grid grid-cols-3 gap-2">
            {presetAmounts.map((presetAmount) => (
              <button
                key={presetAmount}
                onClick={() => {
                  setAmount(presetAmount.toString());
                  setError(null);
                }}
                disabled={loading}
                className={`py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                  amount === presetAmount.toString()
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                    : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                } disabled:opacity-50`}
              >
                ₹{presetAmount}
              </button>
            ))}
          </div>
        </div>

        {/* Custom amount */}
        <div className="mb-6">
          <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
            Custom amount (₹):
          </label>
          <input
            type="number"
            min="1"
            max="100000"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError(null);
            }}
            disabled={loading}
            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
          />
        </div>

        {/* Info text */}
        <div className="mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-700">
            💡 You will receive <strong>{amount}</strong> coins for ₹{amount}
          </p>
        </div>

        {/* Payment Form */}
        {!stripePublicKey ? (
          <div className="p-3 sm:p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg text-sm sm:text-base text-yellow-700">
            Stripe is not configured. Please set REACT_APP_STRIPE_PUBLIC_KEY in
            frontend/.env and restart the app.
          </div>
        ) : (
          <Elements stripe={stripePromise}>
            <PaymentForm
              amount={amount}
              onSuccess={() => {
                onSuccess();
                onClose();
              }}
              onError={setError}
              onLoading={setLoading}
            />
          </Elements>
        )}

        <button
          onClick={onClose}
          disabled={loading}
          className="w-full mt-3 py-2 sm:py-3 px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
        >
          Cancel
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">
          Secure payment by Stripe
        </p>
      </div>
    </div>
  );
};

export default AddCoinsModal;
